import { getComponentEventCapability } from "@cogo/schema/component-event-capabilities"
import { stableJsonSha256 } from "../design-ontology-binding/stable-json-hash"
import { computeSourceBoundInteractionSubject } from "./subject-binding"
import type {
  CompileSourceBoundInteractionContractInput,
  SourceBoundInteraction,
  SourceBoundInteractionContractCompilation,
  SourceBoundInteractionContractIssue,
  SourceBoundInteractionContractIssueCode,
  SourceBoundInteractionContractV1,
  SourceBoundInteractionContractValidation,
  SourceBoundInteractionRelation,
} from "./types"

const CONTRACT_SCHEMA_VERSION =
  "cogo.source-bound-interaction-contract.v1" as const
const CONTRACT_COMPILER =
  "cogo.library-design-source-bound-interaction-compiler.v1" as const
const SHA256_REF = /^sha256:[0-9a-f]{64}$/

type ContractBody = Omit<SourceBoundInteractionContractV1, "contentHash">

/**
 * Compiles only interactions that are closed by the admitted Library and
 * Design Ontology artifacts. No semantic-role, element, event, flow, or state
 * target fallback is allowed: an incomplete source declaration returns a
 * blocked result instead of an executable partial contract.
 */
export async function compileSourceBoundInteractionContract(
  input: CompileSourceBoundInteractionContractInput,
): Promise<SourceBoundInteractionContractCompilation> {
  const issues: SourceBoundInteractionContractIssue[] = []
  const interactions: SourceBoundInteraction[] = []

  let expectedSubject
  try {
    expectedSubject = await computeSourceBoundInteractionSubject(input)
  } catch {
    return {
      status: "blocked",
      issues: [
        issue(
          "source-interaction-subject-mismatch",
          "$.provenance",
          "Source-bound interaction subject artifacts are not canonical JSON data.",
          [],
        ),
      ],
    }
  }
  const subjectMismatches = (
    Object.keys(expectedSubject) as Array<keyof typeof expectedSubject>
  ).filter((key) => input[key] !== expectedSubject[key])
  if (subjectMismatches.length > 0) {
    return {
      status: "blocked",
      issues: [
        issue(
          "source-interaction-subject-mismatch",
          "$.provenance",
          "Source-bound interaction provenance must equal the canonical Library and Design artifact subject.",
          subjectMismatches.flatMap((key) => [
            `${key}:expected:${expectedSubject[key]}`,
            `${key}:received:${input[key]}`,
          ]),
        ),
      ],
    }
  }

  const relationsSnapshot = snapshotCanonicalJsonData(
    input.sourceInteractionRelations ?? [],
  )
  if (!relationsSnapshot.ok || !Array.isArray(relationsSnapshot.value)) {
    return {
      status: "blocked",
      issues: [
        issue(
          "source-interaction-relation-invalid",
          "$.sourceInteractionRelations",
          "Source interaction relations must be canonical JSON data.",
          [],
        ),
      ],
    }
  }
  const blocks = new Map(
    input.library.runtime.blocks.map((block) => [block.blockId, block]),
  )
  const designNodes = new Map(
    input.design.nodes.map((node) => [node.nodeId, node]),
  )
  const transitions = new Map(
    input.library.runtime.uxStatecharts.flatMap((statechart) =>
      statechart.transitions.map((transition) => [
        transition.transitionId,
        { statechart, transition },
      ] as const),
    ),
  )
  for (const [index, candidate] of relationsSnapshot.value.entries()) {
    const relation = admitSourceInteractionRelation(candidate)
    if (!relation) {
      issues.push(
        issue(
          "source-interaction-relation-invalid",
          `$.sourceInteractionRelations[${index}]`,
          "Source interaction relation must contain only its six exact string references.",
          [],
        ),
      )
      continue
    }
    const block = blocks.get(relation.blockId)
    if (!block) {
      issues.push(
        issue(
          "source-interaction-block-missing",
          `$.sourceInteractionRelations[${index}].blockId`,
          "Explicit source interaction relation references no compiled Library block.",
          [relation.blockId],
        ),
      )
      continue
    }
    if (
      block.semanticNodeId !== relation.sourceNodeId ||
      relation.elementId !== relation.sourceNodeId
    ) {
      issues.push(
        issue(
          "source-interaction-relation-invalid",
          `$.sourceInteractionRelations[${index}].sourceNodeId`,
          "Explicit relation must identify the block's exact semantic node and generated element.",
          [block.semanticNodeId, relation.sourceNodeId, relation.elementId],
        ),
      )
      continue
    }
    const designNode = designNodes.get(relation.sourceNodeId)
    if (!designNode) {
      issues.push(
        issue(
          "source-interaction-design-node-missing",
          `$.sourceInteractionRelations[${index}].sourceNodeId`,
          "Explicit relation references no exact Design Ontology node.",
          [relation.sourceNodeId],
        ),
      )
      continue
    }
    if (designNode.componentRef !== relation.componentRef) {
      issues.push(
        issue(
          "source-interaction-relation-invalid",
          `$.sourceInteractionRelations[${index}].componentRef`,
          "Explicit relation must identify the Design Ontology node's exact component.",
          [designNode.componentRef, relation.componentRef],
        ),
      )
      continue
    }
    const transitionBinding = transitions.get(relation.flowId)
    if (
      !transitionBinding ||
      transitionBinding.statechart.namespaceId !== block.uxInstanceNamespaceId
    ) {
      issues.push(
        issue(
          "source-interaction-flow-missing",
          `$.sourceInteractionRelations[${index}].flowId`,
          "Explicit relation references no transition owned by the block's UX instance.",
          [relation.flowId, block.uxInstanceNamespaceId],
        ),
      )
      continue
    }
    const componentType = rendererComponentType(designNode.rendererRef)
    const capability = getComponentEventCapability(componentType)
    if (
      !capability ||
      !capability.events.some((eventType) => eventType === relation.eventType)
    ) {
      issues.push(
        issue(
          "source-interaction-component-event-unsupported",
          `$.sourceInteractionRelations[${index}].eventType`,
          "Explicit relation event is not admitted by the bound renderer component.",
          [designNode.rendererRef, componentType, relation.eventType],
        ),
      )
      continue
    }
    interactions.push({
      blockId: block.blockId,
      sourceNodeId: relation.sourceNodeId,
      elementId: relation.elementId,
      componentRef: relation.componentRef,
      eventType: relation.eventType,
      flowId: transitionBinding.transition.transitionId,
      stateWrites: [],
      provenance: {
        uxInstanceNamespaceId: transitionBinding.statechart.namespaceId,
        uxPatternRef: transitionBinding.statechart.definitionRef,
        transitionId: transitionBinding.transition.transitionId,
        componentCapabilityType: componentType,
        effectActionIds: [...transitionBinding.transition.effectActionIds],
        emittedEventIds: [...transitionBinding.transition.emittedEventIds],
      },
    })
  }

  const duplicateIssues = duplicateInteractionIssues(interactions)
  issues.push(...duplicateIssues)
  if (issues.length > 0) {
    return {
      status: "blocked",
      issues: orderedIssues(issues),
    }
  }

  const body: ContractBody = {
    schemaVersion: CONTRACT_SCHEMA_VERSION,
    authorityRef: `${input.library.applicationBlueprintRef}#source-bound-interactions`,
    interactions: interactions.sort(compareInteraction),
    provenance: {
      compiler: CONTRACT_COMPILER,
      applicationBlueprintRef: input.library.applicationBlueprintRef,
      applicationInstanceId: input.library.applicationInstanceId,
      designOntologyReleaseRef: expectedSubject.designOntologyReleaseRef,
      applicationSemanticHash: expectedSubject.applicationSemanticHash,
      designBindingHash: expectedSubject.designBindingHash,
      renderContractHash: expectedSubject.renderContractHash,
      uxPatternRefs: [...input.library.provenance.uxPatternRefs],
      sourceRefs: [...input.library.provenance.sourceRefs],
    },
  }
  const contentHash = `sha256:${await stableJsonSha256(body)}` as const

  return {
    status: "admitted",
    contract: {
      ...body,
      contentHash,
    },
  }
}

/**
 * Recompiles the expected authority from its admitted source artifacts and
 * requires byte-independent canonical equality. Extra interactions and extra
 * fields are rejected along with missing or changed bindings.
 */
export async function validateSourceBoundInteractionContract(
  candidate: unknown,
  input: CompileSourceBoundInteractionContractInput,
): Promise<SourceBoundInteractionContractValidation> {
  const snapshot = snapshotCanonicalJsonData(candidate)
  if (!snapshot.ok || !safeIsPlainRecord(snapshot.value)) {
    return invalidContract(
      "source-interaction-contract-shape-invalid",
      "$",
      "Source-bound interaction contract must be a plain JSON object.",
    )
  }
  const contract = snapshot.value

  if (
    contract.schemaVersion !== CONTRACT_SCHEMA_VERSION ||
    typeof contract.authorityRef !== "string" ||
    !contract.authorityRef ||
    !Array.isArray(contract.interactions) ||
    !safeIsPlainRecord(contract.provenance)
  ) {
    return invalidContract(
      "source-interaction-contract-shape-invalid",
      "$",
      "Source-bound interaction contract is missing its closed version, authority, interactions, or provenance fields.",
    )
  }

  const contentHash = contract.contentHash
  if (typeof contentHash !== "string" || !SHA256_REF.test(contentHash)) {
    return invalidContract(
      "source-interaction-contract-shape-invalid",
      "$.contentHash",
      "Source-bound interaction contract contentHash must be a canonical sha256 reference.",
    )
  }

  let bodyHash: string
  try {
    const { contentHash: _contentHash, ...body } = contract
    bodyHash = `sha256:${await stableJsonSha256(body)}`
  } catch {
    return invalidContract(
      "source-interaction-contract-shape-invalid",
      "$",
      "Source-bound interaction contract must contain only canonical JSON values.",
    )
  }
  if (bodyHash !== contentHash) {
    return invalidContract(
      "source-interaction-contract-content-hash-mismatch",
      "$.contentHash",
      "Source-bound interaction contract content does not match its declared hash.",
      [contentHash],
    )
  }

  const expected = await compileSourceBoundInteractionContract(input)
  if (expected.status === "blocked") {
    return { ok: false, issues: expected.issues }
  }

  let candidateHash: string
  let expectedHash: string
  try {
    const hashes = await Promise.all([
      stableJsonSha256(contract),
      stableJsonSha256(expected.contract),
    ])
    candidateHash = hashes[0]
    expectedHash = hashes[1]
  } catch {
    return invalidContract(
      "source-interaction-contract-shape-invalid",
      "$",
      "Source-bound interaction contract must contain only canonical JSON values.",
    )
  }
  if (candidateHash !== expectedHash) {
    return invalidContract(
      "source-interaction-contract-authority-mismatch",
      "$",
      "Source-bound interaction contract does not equal the contract compiled from admitted Library and Design Ontology authority.",
      [expected.contract.authorityRef, expected.contract.contentHash],
    )
  }

  return { ok: true, contract: expected.contract }
}

function duplicateInteractionIssues(
  interactions: SourceBoundInteraction[],
): SourceBoundInteractionContractIssue[] {
  const issues: SourceBoundInteractionContractIssue[] = []
  const seen = new Map<string, SourceBoundInteraction>()
  for (const interaction of interactions) {
    const key = `${interaction.blockId}\u0000${interaction.elementId}\u0000${interaction.eventType}`
    const previous = seen.get(key)
    if (!previous) {
      seen.set(key, interaction)
      continue
    }
    issues.push(
      issue(
        "source-interaction-duplicate",
        "$.interactions",
        "One block element event may be authorized by only one source-bound flow.",
        [previous.flowId, interaction.flowId],
      ),
    )
  }
  return issues
}

function admitSourceInteractionRelation(
  value: unknown,
): SourceBoundInteractionRelation | null {
  if (!safeIsPlainRecord(value)) return null
  const keys = Object.keys(value).sort()
  if (
    keys.join("\u0000") !==
    [
      "blockId",
      "componentRef",
      "elementId",
      "eventType",
      "flowId",
      "sourceNodeId",
    ]
      .sort()
      .join("\u0000")
  ) {
    return null
  }
  if (
    typeof value.blockId !== "string" ||
    !value.blockId ||
    typeof value.sourceNodeId !== "string" ||
    !value.sourceNodeId ||
    typeof value.elementId !== "string" ||
    !value.elementId ||
    typeof value.componentRef !== "string" ||
    !value.componentRef ||
    typeof value.eventType !== "string" ||
    !value.eventType ||
    typeof value.flowId !== "string" ||
    !value.flowId
  ) {
    return null
  }
  return {
    blockId: value.blockId,
    sourceNodeId: value.sourceNodeId,
    elementId: value.elementId,
    componentRef: value.componentRef,
    eventType: value.eventType,
    flowId: value.flowId,
  }
}

function rendererComponentType(rendererRef: string): string {
  const localName = rendererRef.slice(rendererRef.lastIndexOf(":") + 1)
  return localName
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[_\s]+/g, "-")
    .toLowerCase()
}

function compareInteraction(
  left: SourceBoundInteraction,
  right: SourceBoundInteraction,
): number {
  return (
    left.blockId.localeCompare(right.blockId) ||
    left.elementId.localeCompare(right.elementId) ||
    left.eventType.localeCompare(right.eventType) ||
    left.flowId.localeCompare(right.flowId)
  )
}

function orderedIssues(
  issues: SourceBoundInteractionContractIssue[],
): SourceBoundInteractionContractIssue[] {
  return [...issues].sort(
    (left, right) =>
      left.path.localeCompare(right.path) ||
      left.code.localeCompare(right.code) ||
      left.message.localeCompare(right.message),
  )
}

function issue(
  code: SourceBoundInteractionContractIssueCode,
  path: string,
  message: string,
  evidenceRefs: string[],
): SourceBoundInteractionContractIssue {
  return { code, path, message, evidenceRefs: [...new Set(evidenceRefs)] }
}

function invalidContract(
  code: SourceBoundInteractionContractIssueCode,
  path: string,
  message: string,
  evidenceRefs: string[] = [],
): SourceBoundInteractionContractValidation {
  return {
    ok: false,
    issues: [issue(code, path, message, evidenceRefs)],
  }
}

function safeIsPlainRecord(value: unknown): value is Record<string, unknown> {
  try {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false
    const prototype = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
  } catch {
    return false
  }
}

type CanonicalJsonSnapshot =
  | { ok: true; value: unknown }
  | { ok: false }

function snapshotCanonicalJsonData(
  value: unknown,
  ancestors = new Set<object>(),
): CanonicalJsonSnapshot {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return { ok: true, value }
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? { ok: true, value } : { ok: false }
  }
  if (typeof value !== "object") return { ok: false }

  try {
    if (ancestors.has(value)) return { ok: false }
    ancestors.add(value)
    const descriptors = Object.getOwnPropertyDescriptors(value)

    if (Array.isArray(value)) {
      const lengthDescriptor = descriptors.length
      if (!lengthDescriptor || !("value" in lengthDescriptor)) {
        return { ok: false }
      }
      const length = lengthDescriptor.value
      if (!Number.isSafeInteger(length) || length < 0) return { ok: false }
      const result: unknown[] = []
      for (let index = 0; index < length; index += 1) {
        const descriptor = descriptors[String(index)]
        if (
          !descriptor ||
          !("value" in descriptor) ||
          descriptor.enumerable !== true
        ) {
          return { ok: false }
        }
        const item = snapshotCanonicalJsonData(descriptor.value, ancestors)
        if (!item.ok) return item
        result.push(item.value)
      }
      if (
        Object.keys(descriptors).some(
          (key) => key !== "length" && !/^(0|[1-9][0-9]*)$/.test(key),
        )
      ) {
        return { ok: false }
      }
      ancestors.delete(value)
      return { ok: true, value: result }
    }

    const prototype = Object.getPrototypeOf(value)
    if (prototype !== Object.prototype && prototype !== null) {
      return { ok: false }
    }
    const result: Record<string, unknown> = {}
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (!("value" in descriptor) || descriptor.enumerable !== true) {
        return { ok: false }
      }
      const property = snapshotCanonicalJsonData(descriptor.value, ancestors)
      if (!property.ok) return property
      result[key] = property.value
    }
    ancestors.delete(value)
    return { ok: true, value: result }
  } catch {
    return { ok: false }
  }
}
